import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useParams } from "react-router-dom";

import useGetTransitionProposalAnalysisSummary from "@fartherfinance/frontend/api/TransitionProposals/hooks/useGetTransitionProposalAnalysisSummary";
import useGetTransitionProposalHoldings from "@fartherfinance/frontend/api/TransitionProposals/hooks/useGetTransitionProposalHoldings";
import {
  TransitionProposalHoldingsFilterResponse,
  TransitionProposalId,
} from "@fartherfinance/frontend/api/TransitionProposals/types";

import { initialTransitionProposalFlowState } from "../../constants";
import { TransitionProposalFlowStateData } from "../../types";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import BorderBox from "@src/sharedComponents/BorderBox/BorderBox";
import BorderBoxBody from "@src/sharedComponents/BorderBox/BorderBoxBody";
import BorderBoxHeader from "@src/sharedComponents/BorderBox/BorderBoxHeader";
import CardCheckBox from "@src/sharedComponents/CardCheckBox/CardCheckbox";
import { actions } from "@src/sharedComponents/FlowContainer/reducer/actions";
import { useFlowContainerContext } from "@src/sharedComponents/FlowContainer/reducer/Context";
import GridRow from "@src/sharedComponents/GridRow/GridRow";
import ItemList from "@src/sharedComponents/ItemList/ItemList";
import {
  FartherTableContainer,
  FartherTablePagination,
} from "@src/sharedComponents/Table/Components";
import { TableSortOrder } from "@src/sharedComponents/Table/Types";
import Tooltip from "@src/sharedComponents/Tooltip/Tooltip";
import Text from "@src/sharedComponents/Typography/Text";
import { pluralize } from "@src/utils/pluralize";
import toUSD from "@src/utils/toUSD";

import { summaryDataMap } from "./constants";
import EditableTransitionProposalHoldingsTable from "./EditableTransitionProposalHoldingsTable/EditableTransitionProposalHoldingsTable";
import { FilterName } from "./types";

import styles from "./Review.module.css";

const Review = () => {
  const { transitionProposalId } = useParams<{
    transitionProposalId: TransitionProposalId;
  }>();

  const [page, setPage] = useState(1);
  const [pageSize, _setPageSize] = useState(10);
  const auth = useRequestAuth();
  const [orderBy, setOrderBy] = useState<string>("name asc");
  const [allHoldingsFilters, setAllHoldingsFilters] =
    useState<TransitionProposalHoldingsFilterResponse>(
      initialTransitionProposalFlowState.data.holdingsFilters
    );
  const [pagination, setPagination] = useState<{
    currentPage: number;
    totalPages: number;
    pageSize: number;
    totalCount: number;
    prevPage: number | undefined;
    nextPage: number | undefined;
  }>({
    currentPage: 0,
    totalPages: 1,
    pageSize,
    totalCount: 0,
    prevPage: undefined,
    nextPage: undefined,
  });

  const {
    dispatch,
    state: {
      data: { holdingsFilters: holdingsFiltersState },
    },
  } = useFlowContainerContext<Partial<TransitionProposalFlowStateData>>();

  const holdingsFilters = useMemo(() => {
    if (holdingsFiltersState === undefined) {
      return initialTransitionProposalFlowState.data.holdingsFilters;
    }
    return holdingsFiltersState;
  }, [holdingsFiltersState]);

  const filterQueryParams = useMemo(() => {
    return Object.entries(holdingsFilters).reduce((acc, [key, _]) => {
      if (
        holdingsFilters[key as FilterName].length > 0 &&
        holdingsFilters[key as FilterName].some((el) => el !== null)
      ) {
        // For now, the API only accepts a single value for each filter
        return {
          [key]: holdingsFilters[key as FilterName].join(","),
        };
      }
      return acc;
    }, {});
  }, [holdingsFilters]);

  useEffect(() => {
    setPage(1);
  }, [holdingsFilters]);

  const { data: holdingsResponseData, isLoading: isLoadingHoldings } =
    useGetTransitionProposalHoldings(
      transitionProposalId,
      {
        page,
        pageSize,
        orderBy,
        ...filterQueryParams,
      },
      auth
    );

  const {
    data: transitionProposalAnalysisSummaryResponse,
    isLoading: isFetchingTransitionProposalSummary,
  } = useGetTransitionProposalAnalysisSummary(transitionProposalId, auth);

  const holdings = useMemo(
    () => holdingsResponseData?.data || [],
    [holdingsResponseData]
  );

  const summary = useMemo(
    () => transitionProposalAnalysisSummaryResponse?.data.fields ?? [],
    [transitionProposalAnalysisSummaryResponse]
  );

  useEffect(() => {
    if (holdingsResponseData?.filters !== undefined && !isLoadingHoldings) {
      setAllHoldingsFilters(holdingsResponseData.filters);
    }
  }, [holdingsResponseData, isLoadingHoldings, dispatch, holdingsFilters]);

  useEffect(() => {
    if (holdingsResponseData?.pagination !== undefined && !isLoadingHoldings) {
      const totalPages = Math.ceil(
        holdingsResponseData.pagination.totalCount /
          holdingsResponseData.pagination.pageSize
      );

      setPagination({
        currentPage: page,
        totalPages: totalPages,
        pageSize: holdingsResponseData.pagination.pageSize,
        totalCount: holdingsResponseData.pagination.totalCount,
        prevPage: page === 1 ? undefined : page - 1,
        nextPage: page === totalPages ? undefined : page + 1,
      });
    }
  }, [holdingsResponseData, isLoadingHoldings, dispatch, page]);

  const handleOnCheckDocument = useCallback(
    (value: string) => (checked: boolean) => {
      if (checked) {
        const existingFilters = holdingsFilters.fileName;

        const filterKeys = Object.keys(holdingsFilters) as FilterName[];

        const filterToClear = filterKeys.find(
          (key) => key !== "fileName" && holdingsFilters[key].length > 0
        ) as FilterName;

        const selectedFilter = { fileName: [...existingFilters, value] };

        dispatch({
          type: actions.SET_DATA,
          payload: {
            appliedFilter: "fileName",
            documentPreviewFilterClicked: true,
            holdingsFilters: {
              ...holdingsFilters,
              ...selectedFilter,
              [filterToClear as FilterName]: [],
            },
          },
        });
      } else {
        const existingFilters = holdingsFilters?.fileName ?? [];
        const selectedFilter = {
          fileName: existingFilters.filter((item) => item !== value),
        };

        dispatch({
          type: actions.SET_DATA,
          payload: {
            appliedFilter: "fileName",
            documentPreviewFilterClicked: true,
            holdingsFilters: {
              ...holdingsFilters,
              ...selectedFilter,
            },
          },
        });
      }
    },
    [holdingsFilters, dispatch]
  );

  const summaryFields = useMemo(() => {
    return Object.entries(summary).map(([key, value]) => ({
      value,
      label: summaryDataMap[key as keyof typeof summaryDataMap],
      key,
    }));
  }, [summary]);

  const sortOrder = useMemo(
    (): TableSortOrder => (orderBy.includes("asc") ? "asc" : "desc"),
    [orderBy]
  );

  const handleOnSort = (sortBy: string) => {
    const flippedOrder = sortOrder === "asc" ? "desc" : "asc";
    setOrderBy(`${sortBy} ${flippedOrder}`);
  };

  const getTextVariant = (key: string) =>
    key === "summary" ? "bodyBold" : "body";

  return (
    <div className={styles.container}>
      <GridRow
        className={styles.grid}
        maxColumns={2}
        gridAutoRows="max-content"
      >
        <ItemList
          title="Proposal Summary Overview"
          isLoading={isFetchingTransitionProposalSummary}
        >
          {summaryFields.map((item, index) => (
            <ItemList.ItemListItem
              key={index}
              className={toClassName({
                [styles.listItemBorder]: index === summaryFields.length - 1,
              })}
              leftComponent={
                <Text variant={getTextVariant(item.key)}>{item.label}</Text>
              }
              rightComponent={
                <Text variant={getTextVariant(item.key)}>
                  {toUSD(Number(item.value))}
                </Text>
              }
            />
          ))}
        </ItemList>
        {allHoldingsFilters.fileName.length > 0 && (
          <BorderBox disableVerticalScroll>
            <BorderBoxHeader
              className={styles.header}
              start={
                <Text variant="title3" color="text">
                  {pluralize(holdingsFilters.fileName.length, "Document", true)}{" "}
                  Selected
                </Text>
              }
            />
            <BorderBoxBody className={styles.documentBody}>
              <GridRow
                gridAutoRows="100%"
                maxColumns={
                  allHoldingsFilters.fileName.length > 1
                    ? allHoldingsFilters.fileName.length
                    : 2
                }
                className={styles.documentGrid}
              >
                {allHoldingsFilters.fileName.map((doc) => (
                  <CardCheckBox
                    key={doc}
                    label={
                      <Tooltip tooltipText={doc} placement="top">
                        <Text
                          className={styles.documentCheckboxLabel}
                          variant="body"
                          color="text"
                        >
                          {doc}
                        </Text>
                      </Tooltip>
                    }
                    checked={
                      holdingsFilters !== undefined
                        ? holdingsFilters?.fileName.includes(doc) ||
                          allHoldingsFilters.fileName.length === 1
                        : false
                    }
                    disabled={allHoldingsFilters.fileName.length === 1}
                    onChange={handleOnCheckDocument(doc)}
                    header={""}
                    formControlClassName={styles.checkbox}
                  />
                ))}
              </GridRow>
            </BorderBoxBody>
          </BorderBox>
        )}
      </GridRow>
      <>
        <FartherTableContainer className={styles.tablePaginationContainer}>
          <EditableTransitionProposalHoldingsTable
            holdings={holdings}
            onSort={handleOnSort}
            sortOrder={sortOrder}
            filters={allHoldingsFilters}
          />
          {pagination && (
            <FartherTablePagination
              pageSize={pagination.pageSize}
              currentPage={pagination.currentPage - 1}
              totalCount={pagination.totalCount}
              onPageChange={(page) => setPage(page + 1)}
              isPrevButtonDisabled={pagination.prevPage === undefined}
              isNextButtonDisabled={pagination.nextPage === undefined}
            />
          )}
        </FartherTableContainer>
      </>
    </div>
  );
};

export default Review;
